import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

/**
 * @typedef {{
 *   variantId: number,
 *   productId: number,
 *   productTitle: string,
 *   variantTitle: string,
 *   image: string,
 *   price: number,
 *   compareAt: number,
 *   available: boolean,
 * }} VariantMeta
 */

/**
 * @typedef {{
 *   minItems: number,
 *   discountPercent: number,
 * }} DiscountTier
 */

/**
 * @typedef {object} BundleBuilderRefs
 * @property {HTMLElement} progressText
 * @property {HTMLUListElement} slotList
 * @property {HTMLElement} extraItems
 * @property {HTMLElement} compareRow
 * @property {HTMLElement} compareTotal
 * @property {HTMLElement} discountedTotal
 * @property {HTMLElement} discountValue
 * @property {HTMLElement} savingsRow
 * @property {HTMLElement} savingsLabel
 * @property {HTMLElement} savingsTotal
 * @property {HTMLElement} mobileSummaryCount
 * @property {HTMLElement} mobileSummaryDiscount
 * @property {HTMLElement} mobileSummaryTotal
 * @property {HTMLElement} mobileSummaryFill
 * @property {HTMLButtonElement} desktopAddButton
 * @property {HTMLButtonElement} mobileAddButton
 * @property {HTMLElement} mobileCountText
 * @property {HTMLElement} errorMessage
 * @property {HTMLDialogElement} zoomDialog
 * @property {HTMLImageElement} zoomImage
 * @property {HTMLButtonElement} zoomPrev
 * @property {HTMLButtonElement} zoomNext
 * @property {HTMLElement} zoomCounter
 * @property {HTMLElement} liveRegion
 */
class BundleBuilderComponent extends Component {
  requiredRefs = [
    'progressText',
    'slotList',
    'extraItems',
    'compareRow',
    'compareTotal',
    'discountedTotal',
    'discountValue',
    'savingsRow',
    'savingsLabel',
    'savingsTotal',
    'mobileSummaryCount',
    'mobileSummaryDiscount',
    'mobileSummaryTotal',
    'mobileSummaryFill',
    'desktopAddButton',
    'mobileAddButton',
    'mobileCountText',
    'errorMessage',
    'zoomDialog',
    'zoomImage',
    'zoomPrev',
    'zoomNext',
    'zoomCounter',
    'liveRegion',
  ];

  /** @type {Map<number, number>} */
  #variantQuantities = new Map();

  /** @type {Map<number, VariantMeta>} */
  #variantMeta = new Map();

  /** @type {Map<number, number>} */
  #variantSortOrder = new Map();

  /** @type {Array<{src: string, alt: string}>} */
  #zoomImages = [];

  /** @type {number} */
  #zoomIndex = 0;

  /** @type {HTMLElement | null} */
  #lastZoomTrigger = null;

  /** @type {number | null} */
  #zoomTouchStartX = null;

  /** @type {boolean} */
  #isSubmitting = false;

  /** @type {IntersectionObserver | null} */
  #summaryVisibilityObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#initializeCards();
    this.#sortCardsByAvailability();
    this.#updateSummary();
    this.#setupMobileSummaryObserver();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initializeCards();
    this.#sortCardsByAvailability();
    this.#updateSummary();
    this.#setupMobileSummaryObserver();
  }

  disconnectedCallback() {
    if (typeof super.disconnectedCallback === 'function') {
      super.disconnectedCallback();
    }

    this.#teardownMobileSummaryObserver();
  }

  get minRequiredItems() {
    return this.#getDiscountTiers()[0]?.minItems || 2;
  }

  get moneyFormat() {
    return this.dataset.moneyFormat || '${{amount}}';
  }

  get currency() {
    return this.dataset.currency || 'USD';
  }

  get slotPrefix() {
    return this.dataset.slotPrefix || 'Item';
  }

  get baseSlotCount() {
    return Number(this.dataset.baseSlotCount) || this.minRequiredItems || 3;
  }

  get savingsLabelText() {
    return this.dataset.savingsLabel || 'Estimated savings';
  }

  /** @returns {HTMLElement[]} */
  #cards() {
    return Array.from(this.querySelectorAll('.bundle-builder__product-card'));
  }

  #initializeCards() {
    this.#variantMeta.clear();
    this.#variantSortOrder.clear();

    const currentVariantIds = new Set();
    let order = 0;

    for (const card of this.#cards()) {
      const select = /** @type {HTMLSelectElement | null} */ (card.querySelector('.bundle-builder__variant-select'));
      if (!(select instanceof HTMLSelectElement)) continue;

      for (const option of select.options) {
        const variantId = Number(option.value);
        if (!variantId || this.#variantSortOrder.has(variantId)) continue;

        currentVariantIds.add(variantId);
        this.#variantSortOrder.set(variantId, order);
        order += 1;
      }

      this.#applySelectedOptionToCard(card, select);
    }

    for (const variantId of this.#variantQuantities.keys()) {
      if (!currentVariantIds.has(variantId)) {
        this.#variantQuantities.delete(variantId);
      }
    }
  }

  /** @param {Event} event */
  onVariantChange(event) {
    const select = /** @type {HTMLSelectElement | null} */ (event.target instanceof HTMLSelectElement ? event.target : null);
    if (!select) return;

    const card = select.closest('.bundle-builder__product-card');
    if (!(card instanceof HTMLElement)) return;

    this.#applySelectedOptionToCard(card, select);
    this.#sortCardsByAvailability();
    this.#updateSummary();
  }

  /** @param {Event} event */
  onVariantOptionInteract(event) {
    const control = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-option-position]') : null
    );
    if (!(control instanceof HTMLElement)) return;

    const card = control.closest('.bundle-builder__product-card');
    if (!(card instanceof HTMLElement)) return;

    const masterSelect = /** @type {HTMLSelectElement | null} */ (card.querySelector('.bundle-builder__variant-select'));
    if (!(masterSelect instanceof HTMLSelectElement)) return;

    const nextOption = this.#findMatchingVariantOption(card, masterSelect, control);
    if (!(nextOption instanceof HTMLOptionElement)) return;

    masterSelect.value = nextOption.value;
    this.#applySelectedOptionToCard(card, masterSelect);
    this.#updateSummary();
  }

  /** @param {Event} event */
  onQuantityButtonClick(event) {
    const button = /** @type {HTMLButtonElement | null} */ (
      event.target instanceof Element ? event.target.closest('.bundle-builder__quantity-button') : null
    );

    if (!(button instanceof HTMLButtonElement)) return;

    const card = button.closest('.bundle-builder__product-card');
    if (!(card instanceof HTMLElement)) return;

    const select = /** @type {HTMLSelectElement | null} */ (card.querySelector('.bundle-builder__variant-select'));
    if (!(select instanceof HTMLSelectElement)) return;

    const selectedOption = select.selectedOptions[0];
    if (!(selectedOption instanceof HTMLOptionElement)) return;

    const variantId = Number(selectedOption.value);
    if (!variantId) return;

    const action = button.dataset.action;
    const currentQuantity = this.#variantQuantities.get(variantId) || 0;

    if (action === 'increment') {
      if (selectedOption.dataset.available !== 'true') return;
      this.#variantQuantities.set(variantId, currentQuantity + 1);
    }

    if (action === 'decrement') {
      if (currentQuantity <= 0) return;

      if (currentQuantity === 1) {
        this.#variantQuantities.delete(variantId);
      } else {
        this.#variantQuantities.set(variantId, currentQuantity - 1);
      }
    }

    this.#syncCardQuantity(card, variantId);
    this.#updateSummary(true);
  }

  /** @param {Event} event */
  onRemoveSlotItem(event) {
    const button = /** @type {HTMLButtonElement | null} */ (
      event.target instanceof Element ? event.target.closest('.bundle-builder__slot-remove') : null
    );

    if (!(button instanceof HTMLButtonElement)) return;

    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    const currentQuantity = this.#variantQuantities.get(variantId) || 0;
    if (currentQuantity <= 0) return;

    if (currentQuantity === 1) {
      this.#variantQuantities.delete(variantId);
    } else {
      this.#variantQuantities.set(variantId, currentQuantity - 1);
    }

    for (const card of this.#cards()) {
      if (Number(card.dataset.currentVariantId) === variantId) {
        this.#syncCardQuantity(card, variantId);
      }
    }

    this.#updateSummary(true);
  }

  /** @param {Event} event */
  async handleAddToCart(event) {
    event.preventDefault();

    if (this.#isSubmitting) return;

    const totals = this.#calculateTotals();
    if (totals.totalQuantity < 1) {
      this.#showError(this.#getMinItemErrorMessage());
      return;
    }

    const items = this.#getSelectedVariants().map(({ variantId, quantity }) => ({
      id: variantId,
      quantity,
    }));

    if (items.length === 0) {
      this.#showError(this.#getMinItemErrorMessage());
      return;
    }

    this.#isSubmitting = true;
    this.#clearError();
    this.#updateSummary();

    const deferredCartUpdate = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map(({ id, quantity }) => ({
          merchandiseId: id,
          quantity,
        })),
        promise: deferredCartUpdate.promise,
        detail: {
          source: 'bundle-builder',
          sourceId: this.dataset.sectionId || this.id || 'bundle-builder',
        },
      })
    );

    try {
      const sections = this.#getSectionIds();
      const payload = {
        items,
        sections: sections.join(','),
        sections_url: window.location.pathname,
      };

      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: JSON.stringify(payload) }));
      const data = await response.json();

      if (!response.ok || data.status) {
        const message =
          (typeof data.description === 'string' && data.description) ||
          (typeof data.message === 'string' && data.message) ||
          'Unable to add items to cart.';
        throw new Error(message);
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!cartResponse.ok) {
        throw new Error(`Failed to refresh cart after adding bundle: ${cartResponse.status}`);
      }

      const cart = await cartResponse.json();
      deferredCartUpdate.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          source: 'bundle-builder',
          sourceId: this.dataset.sectionId || this.id || 'bundle-builder',
          itemCount: cart.item_count,
          sections: data.sections,
          items: cart.items,
          didError: false,
        },
      });

      this.#clearSelections();
      this.#announce('Bundle added to cart.');
      this.#openCartDestination();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to add items to cart.';
      deferredCartUpdate.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: message,
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#showError(message);
    } finally {
      this.#isSubmitting = false;
      this.#updateSummary();
    }
  }

  /** @param {Event} event */
  openZoom(event) {
    const trigger = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('.bundle-builder__zoom-trigger') : null
    );
    if (!(trigger instanceof HTMLElement)) return;

    const card = trigger.closest('.bundle-builder__product-card');
    if (!(card instanceof HTMLElement)) return;

    this.#lastZoomTrigger = trigger;
    this.#zoomImages = this.#collectZoomImages(card);
    this.#zoomIndex = 0;

    if (this.#zoomImages.length === 0) return;

    this.#renderZoomImage();
    this.refs.zoomDialog.showModal();
  }

  /** @param {Event} event */
  closeZoom(event) {
    event.preventDefault();

    if (this.refs.zoomDialog.open) {
      this.refs.zoomDialog.close();
    }

    if (this.#lastZoomTrigger instanceof HTMLElement) {
      this.#lastZoomTrigger.focus();
    }
  }

  /** @param {Event} event */
  onZoomDialogClick(event) {
    if (event.target === this.refs.zoomDialog) {
      this.closeZoom(event);
    }
  }

  /** @param {KeyboardEvent} event */
  onZoomDialogKeydown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.showNextZoomImage(event);
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.showPreviousZoomImage(event);
    }
  }

  /** @param {Event} event */
  showNextZoomImage(event) {
    event.preventDefault();

    if (this.#zoomImages.length <= 1) return;

    this.#zoomIndex = (this.#zoomIndex + 1) % this.#zoomImages.length;
    this.#renderZoomImage();
  }

  /** @param {Event} event */
  showPreviousZoomImage(event) {
    event.preventDefault();

    if (this.#zoomImages.length <= 1) return;

    this.#zoomIndex = (this.#zoomIndex - 1 + this.#zoomImages.length) % this.#zoomImages.length;
    this.#renderZoomImage();
  }

  /** @param {TouchEvent} event */
  onZoomTouchStart(event) {
    this.#zoomTouchStartX = event.changedTouches[0]?.clientX ?? null;
  }

  /** @param {TouchEvent} event */
  onZoomTouchEnd(event) {
    const touchEndX = event.changedTouches[0]?.clientX ?? null;
    if (this.#zoomTouchStartX === null || touchEndX === null) return;

    const deltaX = touchEndX - this.#zoomTouchStartX;
    this.#zoomTouchStartX = null;

    if (Math.abs(deltaX) < 40) return;

    if (deltaX < 0) {
      this.showNextZoomImage(event);
      return;
    }

    this.showPreviousZoomImage(event);
  }

  /** @param {HTMLElement} card */
  #collectZoomImages(card) {
    const imageNodes = card.querySelectorAll('.bundle-builder__media-source');
    const images = [];
    const currentMediaAlt = (card.dataset.currentMediaAlt || '').trim();

    for (const imageNode of imageNodes) {
      if (!(imageNode instanceof HTMLImageElement) || !imageNode.src) continue;

      images.push({
        src: imageNode.src,
        alt: imageNode.alt || card.dataset.productTitle || 'Product image',
      });
    }

    if (images.length > 0) {
      if (currentMediaAlt) {
        const matchingImages = images.filter((image) => (image.alt || '').trim() === currentMediaAlt);
        if (matchingImages.length > 0) {
          return matchingImages;
        }
      }

      return images;
    }

    const fallbackImage = /** @type {HTMLImageElement | null} */ (card.querySelector('.bundle-builder__image'));
    if (fallbackImage?.src) {
      return [{ src: fallbackImage.src, alt: fallbackImage.alt || card.dataset.productTitle || 'Product image' }];
    }

    return images;
  }

  #renderZoomImage() {
    const current = this.#zoomImages[this.#zoomIndex];
    if (!current) return;

    this.refs.zoomDialog.classList.add('is-loading');
    this.refs.zoomImage.onload = () => {
      this.refs.zoomDialog.classList.remove('is-loading');
    };
    this.refs.zoomImage.onerror = () => {
      this.refs.zoomDialog.classList.remove('is-loading');
    };
    this.refs.zoomImage.src = current.src;
    this.refs.zoomImage.alt = current.alt;
    this.refs.zoomCounter.textContent = `${this.#zoomIndex + 1} / ${this.#zoomImages.length}`;

    if (this.refs.zoomImage.complete) {
      this.refs.zoomDialog.classList.remove('is-loading');
    }

    const disableNav = this.#zoomImages.length <= 1;
    this.refs.zoomPrev.disabled = disableNav;
    this.refs.zoomNext.disabled = disableNav;
  }

  #clearSelections() {
    this.#variantQuantities.clear();

    for (const card of this.#cards()) {
      const variantId = Number(card.dataset.currentVariantId);
      if (!variantId) continue;

      this.#syncCardQuantity(card, variantId);
    }

    this.#updateSummary();
  }

  #openCartDestination() {
    const cartDrawer = document.querySelector('cart-drawer-component');

    if (cartDrawer && 'open' in cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return;
    }

    if (Theme?.routes?.cart_url) {
      window.location.href = Theme.routes.cart_url;
    }
  }

  #applySelectedOptionToCard(card, select) {
    const selectedOption = select.selectedOptions[0];
    if (!(selectedOption instanceof HTMLOptionElement)) return;

    const variantId = Number(selectedOption.value);
    if (!variantId) return;

    const price = Number(selectedOption.dataset.price || 0);
    const compareAt = Number(selectedOption.dataset.compareAt || 0);
    const available = selectedOption.dataset.available === 'true';
    const variantTitle = selectedOption.dataset.title || card.dataset.productTitle || '';

    const variantMeta = {
      variantId,
      productId: Number(card.dataset.productId || 0),
      productTitle: card.dataset.productTitle || '',
      variantTitle,
      image: selectedOption.dataset.image || '',
      price,
      compareAt,
      available,
    };

    this.#variantMeta.set(variantId, variantMeta);
    card.dataset.currentVariantId = String(variantId);
    card.dataset.currentMediaAlt = selectedOption.dataset.mediaAlt || '';
    this.#syncVariantControls(card, selectedOption);

    const currentPrice = card.querySelector('[data-role="current-price"]');
    if (currentPrice instanceof HTMLElement) {
      currentPrice.textContent = this.#formatMoney(price);
    }

    const comparePrice = card.querySelector('[data-role="compare-price"]');
    if (comparePrice instanceof HTMLElement) {
      comparePrice.textContent = this.#formatMoney(compareAt);
      comparePrice.classList.toggle('hidden', !(compareAt > price));
    }

    const image = card.querySelector('.bundle-builder__image');
    if (image instanceof HTMLImageElement && selectedOption.dataset.image) {
      image.src = selectedOption.dataset.image;

      const imageSrcset = selectedOption.dataset.imageSrcset || '';
      if (imageSrcset) {
        image.srcset = imageSrcset;
      } else {
        image.removeAttribute('srcset');
      }

      image.alt = variantTitle || card.dataset.productTitle || '';
    }

    card.classList.toggle('is-unavailable', !available);

    const soldOutBadge = card.querySelector('[data-role="sold-out-badge"]');
    if (soldOutBadge instanceof HTMLElement) {
      soldOutBadge.hidden = available;
      soldOutBadge.classList.toggle('hidden', available);
    }

    const availabilityMessage = card.querySelector('[data-role="availability-message"]');
    if (availabilityMessage instanceof HTMLElement) {
      availabilityMessage.hidden = available;
      availabilityMessage.classList.toggle('hidden', available);
    }

    this.#syncCardQuantity(card, variantId);
  }

  #sortCardsByAvailability() {
    const grid = this.querySelector('.bundle-builder__grid');
    if (!(grid instanceof HTMLElement)) return;

    const cards = Array.from(grid.querySelectorAll('.bundle-builder__product-card'));
    if (cards.length <= 1) return;

    const sortedCards = cards
      .map((card, index) => ({
        card,
        index,
        unavailable: card.classList.contains('is-unavailable'),
      }))
      .sort((first, second) => {
        if (first.unavailable === second.unavailable) {
          return first.index - second.index;
        }

        return Number(first.unavailable) - Number(second.unavailable);
      })
      .map(({ card }) => card);

    grid.append(...sortedCards);
  }

  /**
   * @param {HTMLElement} card
   * @param {HTMLOptionElement} changedControl
   */
  #findMatchingVariantOption(card, masterSelect, changedControl) {
    const desiredSelections = new Map();
    const controls = card.querySelectorAll('[data-option-position]');

    for (const control of controls) {
      if (!(control instanceof HTMLElement)) continue;

      const position = Number(control.dataset.optionPosition || 0);
      if (!position) continue;

      if (control instanceof HTMLSelectElement) {
        desiredSelections.set(position, control === changedControl ? control.value : control.value);
        continue;
      }

      if (control.classList.contains('bundle-builder__option-card')) {
        if (control === changedControl) {
          desiredSelections.set(position, control.dataset.optionValue || '');
          continue;
        }

        if (control.classList.contains('is-active')) {
          desiredSelections.set(position, control.dataset.optionValue || '');
        }
      }
    }

    const variantOptions = Array.from(masterSelect.options);

    const exactAvailableMatch = variantOptions.find((option) => {
      if (option.dataset.available !== 'true') return false;
      return this.#optionMatchesSelections(option, desiredSelections);
    });
    if (exactAvailableMatch) return exactAvailableMatch;

    const exactMatch = variantOptions.find((option) => this.#optionMatchesSelections(option, desiredSelections));
    if (exactMatch) return exactMatch;

    let bestMatch = variantOptions[0];
    let bestScore = -1;

    for (const option of variantOptions) {
      let score = 0;

      for (const [position, value] of desiredSelections.entries()) {
        if ((option.dataset[`option${position}`] || '') === value) {
          score += 1;
        }
      }

      if (score > bestScore) {
        bestMatch = option;
        bestScore = score;
        continue;
      }

      if (score === bestScore && option.dataset.available === 'true' && bestMatch?.dataset.available !== 'true') {
        bestMatch = option;
      }
    }

    return bestMatch;
  }

  #optionMatchesSelections(option, desiredSelections) {
    for (const [position, value] of desiredSelections.entries()) {
      if ((option.dataset[`option${position}`] || '') !== value) {
        return false;
      }
    }

    return true;
  }

  #syncVariantControls(card, selectedOption) {
    const controls = card.querySelectorAll('[data-option-position]');

    for (const control of controls) {
      if (!(control instanceof HTMLElement)) continue;

      const position = Number(control.dataset.optionPosition || 0);
      if (!position) continue;

      const selectedValue = selectedOption.dataset[`option${position}`] || '';

      if (control instanceof HTMLSelectElement) {
        control.value = selectedValue;
        continue;
      }

      if (control.classList.contains('bundle-builder__option-card')) {
        const isActive = (control.dataset.optionValue || '') === selectedValue;
        control.classList.toggle('is-active', isActive);
        control.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      }
    }
  }

  #syncCardQuantity(card, variantId) {
    const quantityInput = card.querySelector('[data-role="quantity-input"]');
    const decreaseButton = card.querySelector('[data-action="decrement"]');
    const increaseButton = card.querySelector('[data-action="increment"]');
    const quantityGroup = card.querySelector('[data-role="quantity-group"]');

    const quantity = this.#variantQuantities.get(variantId) || 0;
    const variantMeta = this.#variantMeta.get(variantId);

    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = String(quantity);
    }

    if (decreaseButton instanceof HTMLButtonElement) {
      decreaseButton.disabled = quantity <= 0;
    }

    if (increaseButton instanceof HTMLButtonElement) {
      increaseButton.disabled = variantMeta?.available === false;
    }

    if (quantityGroup instanceof HTMLElement) {
      const isUnavailable = variantMeta?.available === false;
      quantityGroup.classList.toggle('is-disabled', isUnavailable);
      quantityGroup.setAttribute('aria-disabled', isUnavailable ? 'true' : 'false');
    }

    card.classList.toggle('is-selected', quantity > 0);
  }

  #calculateTotals() {
    let totalQuantity = 0;
    let subtotal = 0;

    for (const { variantId, quantity } of this.#getSelectedVariants()) {
      const meta = this.#variantMeta.get(variantId);
      if (!meta) continue;

      totalQuantity += quantity;
      subtotal += meta.price * quantity;
    }

    const tierState = this.#resolveTierState(totalQuantity);
    const qualifiesForDiscount = Boolean(tierState.activeTier);
    const activeDiscountPercent = tierState.activeTier?.discountPercent || 0;
    const discountedTotal = qualifiesForDiscount
      ? Math.round(subtotal * (100 - activeDiscountPercent) / 100)
      : subtotal;

    const savings = Math.max(0, subtotal - discountedTotal);

    return {
      totalQuantity,
      subtotal,
      discountedTotal,
      savings,
      activeDiscountPercent,
      qualifiesForDiscount,
      ...tierState,
    };
  }

  #resolveTierState(totalQuantity) {
    const tiers = this.#getDiscountTiers();
    const minimumTier = tiers[0];
    /** @type {DiscountTier | null} */
    let activeTier = null;
    /** @type {DiscountTier | null} */
    let nextTier = null;

    for (const tier of tiers) {
      if (totalQuantity >= tier.minItems) {
        activeTier = tier;
        continue;
      }

      nextTier = tier;
      break;
    }

    return {
      minimumTier,
      activeTier,
      nextTier,
    };
  }

  #getDiscountTiers() {
    /** @type {DiscountTier[]} */
    const tiers = [];

    const tier1Min = Number(
      this.getAttribute('data-tier1-min-items')
      || this.getAttribute('data-tier-1-min-items')
      || this.dataset.minItems
      || 0
    );
    const tier1Discount = Number(
      this.getAttribute('data-tier1-discount-percent')
      || this.getAttribute('data-tier-1-discount-percent')
      || this.dataset.discountPercent
      || 0
    );
    if (tier1Min > 0 && tier1Discount > 0) {
      tiers.push({
        minItems: tier1Min,
        discountPercent: tier1Discount,
      });
    }

    const enableTier2 = (this.getAttribute('data-enable-tier-2') || this.dataset.enableTier2 || 'true') !== 'false';
    const tier2Min = Number(
      this.getAttribute('data-tier2-min-items')
      || this.getAttribute('data-tier-2-min-items')
      || 0
    );
    const tier2Discount = Number(
      this.getAttribute('data-tier2-discount-percent')
      || this.getAttribute('data-tier-2-discount-percent')
      || 0
    );
    if (enableTier2 && tier2Min > 0 && tier2Discount > 0) {
      tiers.push({
        minItems: tier2Min,
        discountPercent: tier2Discount,
      });
    }

    const enableTier3 = (this.getAttribute('data-enable-tier-3') || this.dataset.enableTier3 || 'true') !== 'false';
    const tier3Min = Number(
      this.getAttribute('data-tier3-min-items')
      || this.getAttribute('data-tier-3-min-items')
      || 0
    );
    const tier3Discount = Number(
      this.getAttribute('data-tier3-discount-percent')
      || this.getAttribute('data-tier-3-discount-percent')
      || 0
    );
    if (enableTier3 && tier3Min > 0 && tier3Discount > 0) {
      tiers.push({
        minItems: tier3Min,
        discountPercent: tier3Discount,
      });
    }

    if (tiers.length === 0) {
      tiers.push({
        minItems: 2,
        discountPercent: 10,
      });
    }

    tiers.sort((first, second) => {
      if (first.minItems !== second.minItems) return first.minItems - second.minItems;
      return first.discountPercent - second.discountPercent;
    });

    /** @type {DiscountTier[]} */
    const deduped = [];
    for (const tier of tiers) {
      const existing = deduped.find((item) => item.minItems === tier.minItems);
      if (!existing) {
        deduped.push({ ...tier });
        continue;
      }

      if (tier.discountPercent > existing.discountPercent) {
        existing.discountPercent = tier.discountPercent;
      }
    }

    return deduped;
  }

  #getSelectedVariants() {
    return Array.from(this.#variantQuantities.entries())
      .filter(([, quantity]) => quantity > 0)
      .map(([variantId, quantity]) => ({
        variantId,
        quantity,
      }))
      .sort((first, second) => {
        const firstOrder = this.#variantSortOrder.get(first.variantId) ?? 0;
        const secondOrder = this.#variantSortOrder.get(second.variantId) ?? 0;
        return firstOrder - secondOrder;
      });
  }

  /** @param {boolean} [announceSelectionUpdate] */
  #updateSummary(announceSelectionUpdate = false) {
    const totals = this.#calculateTotals();

    this.refs.discountedTotal.textContent = this.#formatMoney(totals.discountedTotal);
    this.refs.compareTotal.textContent = this.#formatMoney(totals.subtotal);
    this.refs.savingsTotal.textContent = this.#formatMoney(totals.savings);
    this.refs.discountValue.textContent = this.#buildAppliedDiscountValue(totals);
    this.refs.savingsLabel.textContent = this.#buildSavingsLabel(totals);
    this.refs.mobileSummaryCount.textContent = this.#buildMobileSummaryCount(totals.totalQuantity);
    this.refs.mobileSummaryDiscount.textContent = this.#buildAppliedDiscountValue(totals);
    this.refs.mobileSummaryTotal.textContent = this.#formatMoney(totals.discountedTotal);
    this.refs.mobileSummaryFill.style.width = `${this.#buildMobileSummaryFill(totals.totalQuantity)}%`;

    this.refs.compareRow.hidden = !(totals.qualifiesForDiscount && totals.savings > 0);
    this.refs.savingsRow.hidden = !(totals.qualifiesForDiscount && totals.savings > 0);

    this.refs.progressText.textContent = this.#buildProgressText(totals);
    this.refs.mobileCountText.textContent = `${totals.totalQuantity} selected`;
    this.#updateOfferTierCards(totals);

    const ctaEnabled = totals.totalQuantity >= 1 && !this.#isSubmitting;
    this.refs.desktopAddButton.disabled = !ctaEnabled;
    this.refs.mobileAddButton.disabled = !ctaEnabled;

    this.#renderSlots();

    if (announceSelectionUpdate) {
      this.#announce(this.#buildLiveSelectionMessage(totals.totalQuantity));
    }
  }

  #renderSlots() {
    const selectedUnits = this.#expandSelectionsIntoUnits();
    const targetSlotCount = this.#getVisibleSlotCount(selectedUnits.length);
    this.#ensureSlotCount(targetSlotCount);
    const slots = Array.from(this.refs.slotList.children);

    for (let index = 0; index < slots.length; index += 1) {
      const slot = slots[index];
      if (!(slot instanceof HTMLLIElement)) continue;

      const unit = selectedUnits[index];
      if (!unit) {
        this.#renderEmptySlot(slot, index + 1);
        continue;
      }

      this.#renderFilledSlot(slot, unit);
    }

    // Slot grid expands to fit all selected units, so this indicator is no longer needed.
    if (this.refs.extraItems instanceof HTMLElement) {
      this.refs.extraItems.hidden = true;
      this.refs.extraItems.textContent = '';
    }
  }

  /** @param {number} selectedUnitCount */
  #getVisibleSlotCount(selectedUnitCount) {
    const minimumVisibleSlots = Math.min(3, this.baseSlotCount);

    if (selectedUnitCount < minimumVisibleSlots) {
      return minimumVisibleSlots;
    }

    if (selectedUnitCount >= this.baseSlotCount) {
      return this.baseSlotCount;
    }

    return Math.min(this.baseSlotCount, selectedUnitCount + 1);
  }

  /** @param {number} targetCount */
  #ensureSlotCount(targetCount) {
    const list = this.refs.slotList;
    const currentCount = list.children.length;

    if (currentCount < targetCount) {
      for (let index = currentCount; index < targetCount; index += 1) {
        const slot = document.createElement('li');
        slot.className = 'bundle-builder__slot';
        slot.dataset.slotIndex = String(index);
        slot.dataset.placeholder = this.#buildSlotPlaceholder(index + 1);
        list.appendChild(slot);
      }
      return;
    }

    if (currentCount > targetCount) {
      for (let index = currentCount - 1; index >= targetCount; index -= 1) {
        list.children[index]?.remove();
      }
    }
  }

  /** @param {number} index */
  #buildSlotPlaceholder(index) {
    return `${this.slotPrefix} ${index}`;
  }

  #expandSelectionsIntoUnits() {
    const units = [];

    for (const { variantId, quantity } of this.#getSelectedVariants()) {
      const meta = this.#variantMeta.get(variantId);
      if (!meta) continue;

      const displayTitle = meta.productTitle === meta.variantTitle
        ? meta.productTitle
        : `${meta.productTitle} · ${meta.variantTitle}`;

      for (let count = 0; count < quantity; count += 1) {
        units.push({
          variantId,
          title: displayTitle,
          image: meta.image,
        });
      }
    }

    return units;
  }

  /**
   * @param {HTMLLIElement} slot
   * @param {{variantId: number, title: string, image: string}} unit
   */
  #renderFilledSlot(slot, unit) {
    slot.replaceChildren();

    const inner = document.createElement('div');
    inner.className = 'bundle-builder__slot-inner is-filled';

    const media = document.createElement('div');
    media.className = 'bundle-builder__slot-media';

    if (unit.image) {
      const img = document.createElement('img');
      img.src = unit.image;
      img.alt = unit.title;
      media.appendChild(img);
    } else {
      media.textContent = '•';
    }

    const title = document.createElement('p');
    title.className = 'bundle-builder__slot-title';
    title.textContent = unit.title;

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'bundle-builder__slot-remove';
    remove.setAttribute('on:click', '/onRemoveSlotItem');
    remove.dataset.variantId = String(unit.variantId);
    remove.textContent = 'Remove';

    inner.append(media, title, remove);
    slot.appendChild(inner);
  }

  /** @param {HTMLLIElement} slot */
  #renderEmptySlot(slot) {
    const placeholder = slot.dataset.placeholder || 'Item';

    slot.replaceChildren();

    const inner = document.createElement('div');
    inner.className = 'bundle-builder__slot-inner is-empty';

    const media = document.createElement('div');
    media.className = 'bundle-builder__slot-media';
    media.textContent = placeholder.split(' ').pop() || '•';

    const title = document.createElement('p');
    title.className = 'bundle-builder__slot-title';
    title.textContent = placeholder;

    inner.append(media, title);
    slot.appendChild(inner);
  }

  /** @param {{totalQuantity: number, minimumTier: DiscountTier, activeTier: DiscountTier | null, nextTier: DiscountTier | null}} totals */
  #buildProgressText(totals) {
    if (totals.activeTier && totals.nextTier) {
      const remaining = Math.max(0, totals.nextTier.minItems - totals.totalQuantity);
      return `${totals.activeTier.discountPercent}% unlocked. Add ${remaining} more item${remaining === 1 ? '' : 's'} for ${totals.nextTier.discountPercent}% off.`;
    }

    if (totals.activeTier) {
      return `${totals.activeTier.discountPercent}% bundle savings unlocked.`;
    }

    const remaining = Math.max(0, totals.minimumTier.minItems - totals.totalQuantity);
    if (remaining === totals.minimumTier.minItems) {
      return `Add ${totals.minimumTier.minItems} items to unlock ${totals.minimumTier.discountPercent}% off.`;
    }

    return `Add ${remaining} more item${remaining === 1 ? '' : 's'} to unlock ${totals.minimumTier.discountPercent}% off.`;
  }

  /**
   * @param {{activeTier: DiscountTier | null}} totals
   */
  #buildAppliedDiscountValue(totals) {
    if (!totals.activeTier) return '0%';
    return `${totals.activeTier.discountPercent}%`;
  }

  /**
   * @param {{activeTier: DiscountTier | null}} totals
   */
  #buildSavingsLabel(totals) {
    if (!totals.activeTier) return this.savingsLabelText;
    return `${this.savingsLabelText} (${totals.activeTier.discountPercent}% off)`;
  }

  /** @param {number} totalQuantity */
  #buildMobileSummaryCount(totalQuantity) {
    const goal = Math.max(1, this.baseSlotCount);
    return `${totalQuantity} / ${goal}`;
  }

  /** @param {number} totalQuantity */
  #buildMobileSummaryFill(totalQuantity) {
    const goal = Math.max(1, this.baseSlotCount);
    return Math.max(0, Math.min(100, (totalQuantity / goal) * 100));
  }

  /**
   * @param {{totalQuantity: number, activeTier: DiscountTier | null, nextTier: DiscountTier | null}} totals
   */
  #updateOfferTierCards(totals) {
    const cards = this.querySelectorAll('[data-tier-card]');

    for (const card of cards) {
      if (!(card instanceof HTMLElement)) continue;

      const minItems = Number(card.dataset.tierMinItems || 0);
      const discountPercent = Number(card.dataset.tierDiscountPercent || 0);
      const status = card.querySelector('[data-tier-status]');

      let state = 'locked';
      let label = `Add ${Math.max(0, minItems - totals.totalQuantity)} more`;

      if (totals.activeTier && totals.activeTier.minItems === minItems && totals.activeTier.discountPercent === discountPercent) {
        state = 'active';
        label = 'Unlocked';
      } else if (totals.nextTier && totals.nextTier.minItems === minItems && totals.nextTier.discountPercent === discountPercent) {
        state = 'next';
        const remaining = Math.max(0, minItems - totals.totalQuantity);
        label = remaining > 0 ? `${remaining} away` : 'Next up';
      } else if (totals.totalQuantity >= minItems) {
        state = 'achieved';
        label = 'Unlocked';
      }

      card.dataset.tierState = state;

      if (status instanceof HTMLElement) {
        status.textContent = label;
      }
    }
  }

  /** @param {number} totalQuantity */
  #buildLiveSelectionMessage(totalQuantity) {
    const template = this.dataset.liveSelectionTemplate || '{{ count }} items selected';
    return template.replace('{{ count }}', String(totalQuantity));
  }

  #getMinItemErrorMessage() {
    const template = this.dataset.errorMinItems || 'Select at least {{ min }} items to continue.';
    return template.replace('{{ min }}', String(this.minRequiredItems));
  }

  /** @param {number} value */
  #formatMoney(value) {
    return formatMoney(value, this.moneyFormat, this.currency);
  }

  #clearError() {
    this.refs.errorMessage.hidden = true;
    this.refs.errorMessage.textContent = '';
  }

  /** @param {string} message */
  #showError(message) {
    this.refs.errorMessage.hidden = false;
    this.refs.errorMessage.textContent = message;
    this.#announce(message);
  }

  /** @param {string} message */
  #announce(message) {
    this.refs.liveRegion.textContent = message;
  }

  #getSectionIds() {
    const sectionIds = [];

    if (this.dataset.sectionId) {
      sectionIds.push(this.dataset.sectionId);
    }

    const cartItemComponents = document.querySelectorAll('cart-items-component');
    for (const component of cartItemComponents) {
      if (!(component instanceof HTMLElement)) continue;

      if (component.dataset.sectionId && !sectionIds.includes(component.dataset.sectionId)) {
        sectionIds.push(component.dataset.sectionId);
      }
    }

    return sectionIds;
  }

  #setupMobileSummaryObserver() {
    this.#teardownMobileSummaryObserver();
    this.classList.remove('show-mobile-summary');

    if (!window.matchMedia('(max-width: 749px)').matches) return;
    if (!('IntersectionObserver' in window)) return;

    const summaryCard = this.querySelector('.bundle-builder__summary-card');
    if (!(summaryCard instanceof HTMLElement)) return;

    this.#summaryVisibilityObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;

        if (!window.matchMedia('(max-width: 749px)').matches) {
          this.classList.remove('show-mobile-summary');
          return;
        }

        const fullSummaryVisible = entry.isIntersecting && entry.intersectionRatio > 0.12;
        this.classList.toggle('show-mobile-summary', !fullSummaryVisible);
      },
      {
        threshold: [0, 0.12, 0.3, 1],
      }
    );

    this.#summaryVisibilityObserver.observe(summaryCard);
  }

  #teardownMobileSummaryObserver() {
    if (!this.#summaryVisibilityObserver) return;
    this.#summaryVisibilityObserver.disconnect();
    this.#summaryVisibilityObserver = null;
  }
}

if (!customElements.get('bundle-builder-component')) {
  customElements.define('bundle-builder-component', BundleBuilderComponent);
}
